/**
 * A stored audit row is a refused attempt only when the audit capture wrote it: capture adds
 * `outcome: "failed"` together with the numeric `statusCode` of the refusal (400 or higher).
 * A handler may also store its own `outcome` on a 200 (the destination test's `failed` result
 * ran and changed state), which is not a refused attempt (#962).
 */
export function isRefusedAttemptMetadata(metadata: unknown): boolean {
  if (typeof metadata !== "object" || metadata === null) return false;
  const { outcome, statusCode } = metadata as { outcome?: unknown; statusCode?: unknown };
  return outcome === "failed" && typeof statusCode === "number" && statusCode >= 400;
}

/**
 * Actions whose every row records a failure: a refused sign-in, a rejected token and a
 * permission denial. They are written by `requireAuth` and the error handler, not by the
 * capture, so they never carry the capture's `outcome` and `statusCode` pair.
 */
export const FAILURE_BY_DEFINITION_ACTIONS: ReadonlySet<string> = new Set([
  "auth.sign_in_failed",
  "auth.token_rejected",
  "auth.denied",
]);

/**
 * The `outcome` cell every audit CSV writes (the instance, platform and tenant event exports and
 * every saved report's file, #1373): `failed` when the row records a failed or refused attempt
 * (a capture's refused attempt, an action that is a failure by definition, or a connected app's
 * tool call stored with `outcome: "failed"`), else empty. Empty, never `completed`: the trail
 * knows a row failed, it does not know that every other row succeeded at what the person meant.
 * `refused` is `isRefusedAttemptMetadata` of the row, `storedOutcome` its `metadata.outcome`.
 */
export function auditOutcomeCell(
  action: string,
  refused: boolean,
  storedOutcome: unknown = undefined,
): "failed" | "" {
  if (refused || FAILURE_BY_DEFINITION_ACTIONS.has(action)) return "failed";
  return action.startsWith("mcp.") && storedOutcome === "failed" ? "failed" : "";
}
